import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { EyeOutlined, PlusOutlined, ReloadOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import I18nFieldTabs, { cleanI18n, parseI18n } from '../../components/I18nFieldTabs';
import MarketingFlowRecipients from './MarketingFlowRecipients';

const { Text } = Typography;
const API = '/api/admin/marketing-flows';
const AUDIENCE = {
    no_product: '还没用任何产品',
    checklist_incomplete: '首页引导没做完',
    all: '所有人',
};

/**
 * Lifecycle flows ("weak marketing"): one message N days after sign-up while the audience still matches.
 * Frequency / window / opt-out rules are fixed server-side (LifecycleFlowService); shown in the header tooltip.
 */
export default function MarketingFlows() {
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [rules, setRules] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null = closed, {} = new, row = edit
    const [saving, setSaving] = useState(false);
    const [test, setTest] = useState({ id: null, uid: null });
    const [recipients, setRecipients] = useState(null);

    const err = (e, d) => message.error(e.response?.data?.error || e.response?.data?.message || d);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiClient.get(API);
            setRows(r.data?.data?.items || []);
            setRules(r.data?.data?.rules || null);
        } catch (e) {
            err(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const open = (row) => {
        setEditing(row || {});
        form.resetFields();
        form.setFieldsValue(row
            ? { ...row, i18n: parseI18n(row.i18n) }
            : { audience: 'no_product', delay_days: 3, enabled: false, link: '/' });
    };

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const body = { ...v, link: v.link || '', i18n: cleanI18n(v.i18n) };
            if (editing?.id) await apiClient.put(`${API}/${editing.id}`, body);
            else await apiClient.post(API, body);
            message.success('已保存');
            setEditing(null);
            load();
        } catch (e) {
            err(e, '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (row, enabled) => {
        try {
            await apiClient.post(`${API}/${row.id}/toggle`, { enabled });
            load();
        } catch (e) {
            err(e, '操作失败');
        }
    };

    const preview = async (row) => {
        try {
            const r = await apiClient.get(`${API}/${row.id}/preview`);
            const d = r.data?.data || {};
            Modal.info({ title: row.name, content: `接下来几轮会发给约 ${d.would_send ?? 0} 人（窗口内候选 ${d.candidates ?? 0} 人）` });
        } catch (e) {
            err(e, '预览失败');
        }
    };

    const sendTest = async () => {
        try {
            await apiClient.post(`${API}/${test.id}/test`, { user_id: test.uid });
            message.success('已发送（按该用户的语言和通知设置）');
            setTest({ id: null, uid: null });
        } catch (e) {
            err(e, '发送失败');
        }
    };

    const remove = async (row) => {
        try {
            await apiClient.delete(`${API}/${row.id}`);
            load();
        } catch (e) {
            err(e, '删除失败');
        }
    };

    const rate = (n, total) => (total ? `${n}（${Math.round((n / total) * 100)}%）` : '-');

    const columns = [
        { title: '启用', dataIndex: 'enabled', width: 64, render: (v, r) => <Switch size="small" checked={v} onChange={(on) => toggle(r, on)} /> },
        { title: '名称', dataIndex: 'name', ellipsis: true, render: (v, r) => <Space direction="vertical" size={0}><Text>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{r.title}</Text></Space> },
        { title: '时机', dataIndex: 'delay_days', width: 100, render: (v) => `注册后第 ${v} 天` },
        { title: '受众', dataIndex: 'audience', width: 130, render: (v) => <Tag>{AUDIENCE[v] || v}</Tag> },
        { title: '已发 / 7天', width: 100, render: (_, r) => `${r.sent} / ${r.sent_7d}` },
        {
            title: <Tooltip title="按带追踪的邮件计算。Apple 邮件等会自动加载图片，打开数可能偏高">打开</Tooltip>,
            width: 90,
            render: (_, r) => rate(r.opened, r.tracked),
        },
        { title: '点击', width: 90, render: (_, r) => rate(r.clicked, r.tracked) },
        { title: '退订', dataIndex: 'unsubscribed', width: 60 },
        {
            title: <Tooltip title="收到后开始使用了产品（LLM / 订阅 / 云主机 / 存储桶 / DNS / CDN）">转化</Tooltip>,
            width: 100,
            render: (_, r) => (r.sent ? `${r.converted}（${Math.round((r.converted / r.sent) * 100)}%）` : '-'),
        },
        {
            title: '', width: 180, render: (_, r) => (
                <Space size={2}>
                    <Button size="small" type="link" onClick={() => open(r)}>编辑</Button>
                    <Tooltip title="收件人"><Button size="small" type="text" icon={<TeamOutlined />} onClick={() => setRecipients(r)} /></Tooltip>
                    <Tooltip title="预览人数"><Button size="small" type="text" icon={<EyeOutlined />} onClick={() => preview(r)} /></Tooltip>
                    <Tooltip title="发测试"><Button size="small" type="text" icon={<SendOutlined />} onClick={() => setTest({ id: r.id, uid: null })} /></Tooltip>
                    <Popconfirm title="删除这个营销流？" onConfirm={() => remove(r)}><Button size="small" type="link" danger>删除</Button></Popconfirm>
                </Space>
            ),
        },
    ];

    const rulesTip = rules
        ? `固定规则：每人每 ${rules.min_gap_days} 天最多一封；只发注册时间刚好落在第 N～N+${rules.window_days} 天的人（开启不会补发老用户）；仅已验证邮箱；用户可在通知设置或邮件里一键退订（分类「使用提示」）；发送前再确认一次受众条件。`
        : '';

    return (
        <Card size="small"
            title={<Space>营销流<Tooltip title={rulesTip}><Tag color="green">弱营销</Tag></Tooltip></Space>}
            extra={<Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>新建</Button>
            </Space>}>
            <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} pagination={false} scroll={{ x: 1000 }} />

            <Modal open={!!editing} title={editing?.id ? '编辑营销流' : '新建营销流'} width={680} onCancel={() => setEditing(null)}
                onOk={save} confirmLoading={saving} okText="保存" destroyOnClose>
                <Form form={form} layout="vertical">
                    <Space wrap size={16}>
                        <Form.Item name="name" label="名称" rules={[{ required: true, whitespace: true, max: 100 }]}>
                            <Input style={{ width: 220 }} />
                        </Form.Item>
                        <Form.Item name="delay_days" label="注册后第几天" rules={[{ required: true }]}>
                            <InputNumber min={1} max={365} style={{ width: 110 }} />
                        </Form.Item>
                        <Form.Item name="audience" label="受众" rules={[{ required: true }]}>
                            <Select style={{ width: 170 }} options={Object.entries(AUDIENCE).map(([value, label]) => ({ value, label }))} />
                        </Form.Item>
                        {!editing?.id && (
                            <Form.Item name="enabled" label="立即启用" valuePropName="checked"><Switch /></Form.Item>
                        )}
                    </Space>
                    <I18nFieldTabs
                        fields={[{ key: 'title', label: '标题' }, { key: 'body', label: '正文', type: 'textarea', rows: 8 }]}
                        base={<>
                            <Form.Item name="title" label="标题" tooltip="{username} 会换成用户名" rules={[{ required: true, whitespace: true, max: 255 }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="body" label="正文" rules={[{ required: true, whitespace: true, max: 8000 }]}>
                                <Input.TextArea rows={8} />
                            </Form.Item>
                        </>}
                    />
                    <Form.Item name="link" label="按钮链接" tooltip="控制台路径（如 /）或 https 地址">
                        <Input placeholder="/" />
                    </Form.Item>
                </Form>
            </Modal>

            <MarketingFlowRecipients flow={recipients} onClose={() => setRecipients(null)} />

            <Modal open={!!test.id} title="发测试" onCancel={() => setTest({ id: null, uid: null })} onOk={sendTest} okText="发送"
                okButtonProps={{ disabled: !test.uid }} width={360}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">不看规则，直接发给这个用户（站内信 + 邮件）。</Text>
                    <InputNumber placeholder="用户 ID" min={1} value={test.uid} onChange={(v) => setTest((s) => ({ ...s, uid: v }))} style={{ width: '100%' }} />
                </Space>
            </Modal>
        </Card>
    );
}
